import { useEffect, useRef, useState } from "react";
import { Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";
import { api, errMsg } from "@/lib/api";
import { fcfa, fdate, fdatetime } from "@/lib/format";
import { fileUrl, uploadFile } from "@/lib/upload";
import { Loader, PageTitle } from "@/components/common";

const run = (fn, ok, after) => fn().then((r) => { if (ok) toast.success(ok); after?.(r); }).catch((e) => toast.error(errMsg(e)));
const pick = (kind, accept) => new Promise((resolve) => {
  const i = document.createElement("input");
  i.type = "file";
  if (accept) i.accept = accept;
  i.onchange = async () => (i.files[0] ? resolve(await uploadFile(kind, i.files[0]).catch((e) => { toast.error(errMsg(e)); return null; })) : resolve(null));
  i.click();
});

export function SignaturePad({ onChange }) {
  const c = useRef(null);
  const drawing = useRef(false);
  const strokes = useRef(0);
  useEffect(() => {
    const ctx = c.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  }, []);
  const pos = (e) => {
    const r = c.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (c.current.width / r.width), (e.clientY - r.top) * (c.current.height / r.height)];
  };
  const down = (e) => { e.preventDefault(); c.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = c.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = c.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); };
  const up = () => { if (!drawing.current) return; drawing.current = false; strokes.current += 1; onChange(strokes.current ? c.current.toDataURL("image/png") : ""); };
  const clear = () => { c.current.getContext("2d").clearRect(0, 0, 500, 200); strokes.current = 0; onChange(""); };
  return (
    <div>
      <canvas ref={c} width={500} height={200} data-testid="signature-pad" className="w-full h-32 bg-white border border-mr-border rounded-[3px] touch-none"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
      <button type="button" data-testid="signature-clear" className="text-xs text-mr-muted mt-1" onClick={clear}>Effacer</button>
    </div>
  );
}

export function DraftAssistant({ type, onApply }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async (audio) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/operation-draft", { type, text, audio_file_id: audio || "" });
      setRes(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mr-card space-y-2" data-testid="draft-assistant">
      <p className="font-medium flex items-center gap-2"><Sparkles size={16} /> Assistant de rédaction</p>
      <textarea data-testid="draft-text" className="mr-input min-h-[90px]" placeholder="Décrivez librement votre projet : quoi, pourquoi, combien, quelles étapes…" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button data-testid="draft-run" className="mr-btn-primary px-3" disabled={busy || text.trim().length < 20} onClick={() => go()}>{busy ? "Analyse…" : "Structurer la fiche"}</button>
        <button data-testid="draft-audio" className="mr-btn-secondary px-3" disabled={busy} onClick={async () => { const f = await pick("audio", "audio/*"); if (f) go(f); }}>Depuis un fichier vocal</button>
      </div>
      {res && (
        <div className="space-y-2 text-sm" data-testid="draft-result">
          {res.flagged.length > 0 && <div className="border border-mr-danger p-2" data-testid="draft-flagged"><p className="font-semibold text-mr-danger">Promesses de gain bloquées</p>
            {res.flagged.map((f, k) => <p key={k}>« {f.text} » → {f.rewording}</p>)}</div>}
          {res.missing_fields.length > 0 && <div data-testid="draft-missing"><p className="font-semibold">Champs manquants</p>{res.missing_fields.map((m) => <p key={m}>• {m}</p>)}</div>}
          {res.estimates.length > 0 && <div data-testid="draft-estimates"><p className="font-semibold text-mr-warning">Estimations à confirmer</p>{res.estimates.map((m) => <p key={m}>• {m}</p>)}</div>}
          <button data-testid="draft-apply" className="mr-btn-secondary px-3" onClick={() => { onApply(res); toast.success("Fiche pré-remplie : relisez chaque champ."); }}>Pré-remplir la fiche</button>
        </div>
      )}
    </div>
  );
}

export function Votes({ op, manager }) {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ question: "", weight: "part", days: 7, quorum_percent: 50, majority_percent: 50, schedule: "" });
  const load = () => api.get(`/operations/${op.id}/votes`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);
  const create = () => {
    const old = Object.fromEntries(op.default_schedule.map((l) => [l.rank, l]));
    const schedule = f.schedule.trim() ? f.schedule.split(",").map((p, k) => (old[k + 1]?.trigger === "jalon" ? { percent: Number(p.trim()), trigger: "jalon" } : { percent: Number(p.trim()), offset_days: k * 30 })) : null;
    run(() => api.post(`/operations/${op.id}/votes`, { question: f.question, weight: f.weight, days: Number(f.days), quorum_percent: Number(f.quorum_percent), majority_percent: Number(f.majority_percent), schedule }), "Vote ouvert", load);
  };
  if (!items) return <Loader />;
  return (
    <div className="space-y-3" data-testid="op-votes">
      {items.map((v) => {
        const r = v.result || v.live;
        return (
          <div key={v.id} className="mr-card p-4 space-y-2 text-sm" data-testid="vote-card">
            <p className="font-medium">{v.question}</p>
            <p className="text-xs text-mr-muted">Poids : {v.weight === "part" ? "par part" : "par participant"} · quorum {v.quorum_percent} % · majorité {v.majority_percent} % · {v.status === "ouvert" ? `clôture ${fdatetime(v.ends_at)}` : "clos"}</p>
            {v.schedule && <p className="text-xs">Nouvel échéancier proposé : {v.schedule.map((l) => `${l.percent} %`).join(" + ")}</p>}
            {r && <p className="tabular" data-testid="vote-tally">{Object.entries(r.counts).map(([o, n]) => `${o} : ${n}`).join(" · ")} · participation {r.participation} %</p>}
            {v.status === "clos" && <p className={v.result.passed ? "text-mr-accent font-medium" : "text-mr-danger font-medium"} data-testid="vote-result">{v.result.passed ? "Adopté — décision appliquée" : "Non adopté — aucune modification"}</p>}
            {v.status === "ouvert" && <div className="flex flex-wrap gap-2">{op.my_commitment?.status === "adherent" && v.options.map((o) => <button key={o} data-testid={`vote-option-${o}`} className={v.my_ballot === o ? "mr-btn-primary px-3" : "mr-btn-secondary px-3"} onClick={() => run(() => api.post(`/operations/${op.id}/votes/${v.id}/ballot`, { option: o }), "Vote enregistré", load)}>{o}</button>)}
              {manager && <button data-testid="vote-close" className="mr-btn-ghost px-3" onClick={() => run(() => api.post(`/operations/${op.id}/votes/${v.id}/close`), "Vote clos", load)}>Clore</button>}</div>}
          </div>
        );
      })}
      {manager && (
        <div className="mr-card space-y-2" data-testid="vote-create-form"><p className="font-medium">Ouvrir un vote</p>
          <input data-testid="vote-question" className="mr-input" placeholder="Question" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
          <div className="grid grid-cols-2 gap-2 text-sm">
            <label>Poids<select data-testid="vote-weight" className="mr-input mt-1" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })}><option value="part">Par part</option><option value="participant">Par participant</option></select></label>
            <label>Durée (jours)<input data-testid="vote-days" type="number" className="mr-input mt-1" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} /></label>
            <label>Quorum (%)<input data-testid="vote-quorum" type="number" className="mr-input mt-1" value={f.quorum_percent} onChange={(e) => setF({ ...f, quorum_percent: e.target.value })} /></label>
            <label>Majorité (%)<input data-testid="vote-majority" type="number" className="mr-input mt-1" value={f.majority_percent} onChange={(e) => setF({ ...f, majority_percent: e.target.value })} /></label>
          </div>
          <input data-testid="vote-schedule" className="mr-input" placeholder="Nouvel échéancier si adopté (ex. 25, 25, 50) — facultatif" value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} />
          <p className="text-xs text-mr-muted">Options : Pour / Contre. La décision n'est appliquée que si « Pour » l'emporte avec quorum et majorité.</p>
          <button data-testid="vote-create" className="mr-btn-primary" disabled={f.question.length < 5} onClick={create}>Ouvrir le vote</button>
        </div>
      )}
    </div>
  );
}

export function AdvancedRecon({ op }) {
  const [items, setItems] = useState([]);
  const [p, setP] = useState({ start: "", end: "" });
  const load = () => api.get(`/operations/${op.id}/reconciliations`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);
  const imp = async () => {
    const f = await pick("statement", ".csv,.xlsx,.txt");
    if (f) run(() => api.post(`/operations/${op.id}/reconciliations/import`, { statement_file_id: f, period_start: p.start, period_end: p.end }), "Relevé importé et rapproché", load);
  };
  return (
    <div className="space-y-3" data-testid="op-advanced-recon">
      <p className="text-xs text-mr-muted">Relevé CSV ou Excel : date ; libellé ; montant (négatif pour une sortie). Rapprochement automatique avec les paiements validés et les dépenses.</p>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="text-xs">Du<DateField testId="recon-start" className="mt-1" value={p.start} onChange={(v) => setP({ ...p, start: v })} /></div>
        <div className="text-xs">Au<DateField testId="recon-end" className="mt-1" value={p.end} onChange={(v) => setP({ ...p, end: v })} /></div>
        <button data-testid="recon-import" className="mr-btn-secondary px-3" disabled={!p.start || !p.end} onClick={imp}>Importer le relevé</button>
      </div>
      {items.map((r) => (
        <div key={r.id} className="border border-mr-border p-3 text-sm space-y-1" data-testid="recon-row">
          <p className="font-medium">{fdate(r.period_start)} → {fdate(r.period_end)} · écart <span className={r.gap === 0 ? "text-mr-accent" : "text-mr-danger"} data-testid="recon-gap">{fcfa(r.gap)}</span> · {r.status === "rapprochee" ? "validé par le trésorier" : "à valider"}</p>
          <p className="text-xs text-mr-muted">{r.matched} ligne(s) rapprochée(s)</p>
          {r.unmatched_lines.length > 0 && <div className="text-xs"><b>Relevé sans correspondance :</b>{r.unmatched_lines.map((l, k) => <p key={k}>{fdate(l.date)} · {l.label} · {fcfa(l.amount)}</p>)}</div>}
          {r.unmatched_book.length > 0 && <div className="text-xs"><b>Écritures sans ligne au relevé :</b>{r.unmatched_book.map((b) => <p key={b.id}>{fdate(b.date)} · {b.kind} {b.ref} · {fcfa(b.amount)}</p>)}</div>}
          {r.explanation && <p className="text-xs">Explication : {r.explanation}</p>}
          {r.status !== "rapprochee" && (op.my_roles.includes("tresorier") || op.my_roles.includes("admin")) && <button data-testid="recon-validate" className="mr-btn-primary px-3 text-xs"
            onClick={() => { const ex = r.gap === 0 ? "" : window.prompt("Expliquez l'écart"); if (r.gap !== 0 && !ex) return; run(() => api.post(`/operations/${op.id}/reconciliations/${r.id}/validate`, { explanation: ex || "" }), "Rapprochement validé", load); }}>Valider (trésorier)</button>}
        </div>
      ))}
    </div>
  );
}

export function MilestoneTools({ op, reload }) {
  const jalonRanks = op.default_schedule.filter((l) => l.trigger === "jalon").map((l) => l.rank);
  const isAdmin = op.my_roles.includes("admin");
  return (
    <div className="space-y-2" data-testid="op-milestone-tools">
      {op.milestones.map((m) => (
        <div key={m.id} className="flex flex-wrap items-center gap-2 text-sm border-b border-mr-border py-2" data-testid="milestone-row">
          <span className="flex-1">{m.title} · {{ prevu: "prévu", a_valider: "preuves à valider", atteint: "atteint" }[m.status]}{m.installment_rank ? ` · déclenche la tranche ${m.installment_rank}` : ""}</span>
          {m.status === "prevu" && jalonRanks.length > 0 && !m.installment_rank && <select data-testid="milestone-link" className="mr-input w-44 h-9 py-0" value="" onChange={(e) => e.target.value && run(() => api.post(`/operations/${op.id}/milestones/${m.id}/link`, { installment_rank: Number(e.target.value) }), "Jalon lié à la tranche", reload)}>
            <option value="">Lier à une tranche…</option>{jalonRanks.map((r) => <option key={r} value={r}>Tranche {r}</option>)}</select>}
          {m.status === "a_valider" && (m.proof_file_ids || []).map((f) => <button key={f} className="text-xs text-mr-accent" onClick={async () => window.open(await fileUrl(f), "_blank")}>Preuve</button>)}
          {m.status === "a_valider" && isAdmin && <>
            <button data-testid="milestone-validate" className="mr-btn-primary px-2 text-xs" onClick={() => run(() => api.post(`/operations/${op.id}/milestones/${m.id}/validate`, { approve: true }), "Jalon validé : la tranche est appelée", reload)}>Valider et appeler</button>
            <button data-testid="milestone-reject" className="mr-btn-ghost px-2 text-xs" onClick={() => { const reason = window.prompt("Motif"); if (reason) run(() => api.post(`/operations/${op.id}/milestones/${m.id}/validate`, { approve: false, reason }), "Jalon renvoyé au porteur", reload); }}>Refuser</button></>}
        </div>
      ))}
    </div>
  );
}

export function ImportAssistant() {
  const [product, setProduct] = useState("");
  const [res, setRes] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lc, setLc] = useState({ fob_amount: "", currency: "USD", rate_to_fcfa: 600, freight: "", customs_percent: 20, vat_percent: 18, port_fees_fcfa: "", transit_fcfa: "", inland_transport_fcfa: "", quantity: 1 });
  const [cost, setCost] = useState(null);
  const ask = async (mode) => {
    setBusy(true);
    try {
      setRes({ mode, ...(await api.post("/ai/import/assist", { mode, product, quote_file_ids: quotes })).data });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const num = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, k === "currency" ? v : Number(v) || 0]));
  return (
    <div className="mr-card space-y-3" data-testid="import-assistant">
      <p className="mr-eyebrow">Assistant import</p>
      <input data-testid="import-product" className="mr-input" placeholder="Produit (ex. carreaux 60×60)" value={product} onChange={(e) => setProduct(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button data-testid="import-questions" className="mr-btn-secondary px-3" disabled={busy || product.length < 2} onClick={() => ask("questions")}>Questions à poser au fournisseur</button>
        <button data-testid="import-add-quote" className="mr-btn-ghost px-3" onClick={async () => { const f = await pick("document", ".pdf,.xlsx,.txt,.csv"); if (f) setQuotes([...quotes, f]); }}>Ajouter un devis ({quotes.length})</button>
        <button data-testid="import-compare" className="mr-btn-secondary px-3" disabled={busy || quotes.length < 2 || product.length < 2} onClick={() => ask("compare")}>Comparer les devis</button>
      </div>
      {busy && <p className="text-xs text-mr-muted">Analyse en cours…</p>}
      {res?.mode === "questions" && <div className="text-sm" data-testid="import-questions-result">{(res.questions || []).map((q) => <p key={q}>• {q}</p>)}{(res.red_flags || []).map((q) => <p key={q} className="text-mr-danger">⚠ {q}</p>)}</div>}
      {res?.mode === "compare" && <div className="text-sm overflow-x-auto" data-testid="import-compare-result"><p className="text-xs text-mr-warning mb-1">{res.estimate_notice}</p>
        <table className="w-full text-xs"><tbody>{(res.table || []).map((r, k) => <tr key={k} className="border-t border-mr-border">{Object.values(r).map((v, j) => <td key={j} className="p-1">{String(v)}</td>)}</tr>)}</tbody></table><p className="mt-2">{res.summary}</p></div>}
      <p className="text-sm font-medium pt-2">Coût de revient estimé à Abidjan</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {[["fob_amount", "Prix FOB total"], ["rate_to_fcfa", "Taux de change (FCFA)"], ["freight", "Fret (devise)"], ["customs_percent", "Droits de douane (%)"], ["vat_percent", "TVA (%)"], ["port_fees_fcfa", "Frais portuaires (FCFA)"], ["transit_fcfa", "Transit (FCFA)"], ["inland_transport_fcfa", "Transport local (FCFA)"], ["quantity", "Quantité"]].map(([k, l]) => (
          <label key={k}>{l}<input data-testid={`lc-${k}`} type="number" className="mr-input mt-1" value={lc[k]} onChange={(e) => setLc({ ...lc, [k]: e.target.value })} /></label>))}
        <label>Devise<select className="mr-input mt-1" value={lc.currency} onChange={(e) => setLc({ ...lc, currency: e.target.value })}>{["USD", "CNY", "EUR", "XOF"].map((c) => <option key={c}>{c}</option>)}</select></label>
      </div>
      <button data-testid="lc-run" className="mr-btn-primary w-full" disabled={!lc.fob_amount} onClick={() => run(() => api.post("/ai/import/landed-cost", num(lc)), null, (r) => setCost(r.data))}>Estimer</button>
      {cost && <div className="text-sm tabular" data-testid="lc-result"><p className="text-mr-warning text-xs mb-1">{cost.warning}</p>{cost.lines.map((l) => <p key={l.label} className="flex justify-between"><span>{l.label}</span><span>{fcfa(l.amount)}</span></p>)}
        <p className="flex justify-between font-semibold border-t border-mr-border pt-1"><span>Total estimé</span><span>{fcfa(cost.total)}</span></p><p className="flex justify-between"><span>Par unité (estimation)</span><span>{fcfa(cost.per_unit)}</span></p></div>}
    </div>
  );
}

export function MyAgreements() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/me/agreements").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  if (!items) return <Loader />;
  return (
    <div className="space-y-2" data-testid="my-agreements">
      {items.length === 0 ? <p className="text-sm text-mr-muted">Aucune convention signée pour le moment.</p> : items.map((a) => (
        <div key={a.file_id} className="mr-card p-3 flex items-center justify-between text-sm" data-testid="my-agreement-row"><span>{a.title} · signée le {fdatetime(a.signed_at)}</span>
          <button className="mr-btn-ghost px-2" aria-label="Télécharger" onClick={async () => window.open(await fileUrl(a.file_id), "_blank")}><Download size={15} /></button></div>))}
    </div>
  );
}

export function ConsoleTemplates() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/agreement-templates").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  if (!items) return <Loader />;
  return (
    <div className="mr-fade-up space-y-4">
      <PageTitle eyebrow="Contenu" title="Modèles de convention" />
      {items.map((t) => <TemplateCard key={t.id} t={t} reload={load} />)}
    </div>
  );
}

function TemplateCard({ t, reload }) {
  const [body, setBody] = useState(t.body);
  return (
    <div className="mr-card space-y-2" data-testid="template-card"><p className="font-medium">{t.title} · {t.type}</p>
      <textarea data-testid="template-body" className="mr-input min-h-[180px] text-sm" value={body} onChange={(e) => setBody(e.target.value)} />
      <button data-testid="template-save" className="mr-btn-primary" onClick={() => run(() => api.put(`/admin/agreement-templates/${t.id}`, { type: t.type, title: t.title, body }), "Modèle enregistré", reload)}>Enregistrer</button>
    </div>
  );
}
